import React, { useState, useEffect } from "react";

import { useDropzone } from "react-dropzone";
import { apis } from "../apis"; // Assuming you have a way to fetch data from your backend
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ArrowLeft, 
  Plus, 
  RefreshCw, 
  Edit, 
  Trash2, 
  Star, 
  StarOff,
  Upload, 
  X, 
  ArrowRight
} from "lucide-react";
import "./Stories.css";

// Custom Components
const Card = ({ children, className = "" }) => (
  <div className={`card ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", disabled, className = "", icon }) => (
  <button 
    className={`btn btn-${variant} ${className}`} 
    onClick={onClick} 
    disabled={disabled}
  >
    {icon && <span className="btn-icon">{icon}</span>}
    {children}
  </button>
);

const Input = ({ label, value, onChange, error, helperText, type = "text" }) => (
  <div className="input-group">
    <label>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className={error ? "error" : ""}
    />
    {helperText && <span className="helper-text">{helperText}</span>}
    {error && <span className="error-text">{error}</span>}
  </div>
);

const ToggleSwitch = ({ checked, onChange, label }) => (
  <label className="toggle-switch">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
    />
    <span className="toggle-slider"></span>
    {label && <span className="toggle-label">{label}</span>}
  </label>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

const StoryCard = ({ story, onEdit, onDelete, onTogglePremium, onViewReviews }) => {
  const [fileSize, setFileSize] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFileSize = async () => {
      try {
        const response = await fetch(story.story, { method: 'HEAD' });
        const size = response.headers.get('content-length');
        console.log(size);
        if (size) {
          const sizeInMB = (parseInt(size) / (1024 * 1024)).toFixed(2);
          setFileSize(sizeInMB);
        }
      } catch (error) {
        console.error('Error fetching file size:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFileSize();
  }, [story.story]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="story-card">
      <div className="story-preview">
        <img src={story.longImage} alt="Background" className="story-background" />
        <div className="story-overlay">
          <div className="story-header">
            <h3>{story.name}</h3>
            <div className="premium-badge" onClick={(e) => e.stopPropagation()}>
              <ToggleSwitch
                checked={story.premium}
                onChange={(e) => {
                  e.stopPropagation();
                  onTogglePremium(story.id, story.premium);
                }}
                label="مميز"
              />
            </div>
          </div>
          <div className="story-details">
            <div className="story-thumbnail">
              <img src={story.image} alt={story.name} />
            </div>
            <div className="story-info">
              <div className="info-item">
                <span className="info-label">حجم الملف:</span>
                <span className="info-value">
                  {loading ? (
                    <div className="loading-spinner small"></div>
                  ) : (
                    `${fileSize} MB`
                  )}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">تاريخ الإضافة:</span>
                <span className="info-value">{formatDate(story.createdAt)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">آخر تحديث:</span>
                <span className="info-value">{formatDate(story.updatedAt)}</span>
              </div>
              
            </div>
          </div>
          <div className="story-audio" onClick={(e) => e.stopPropagation()}>
            <audio controls>
              <source src={story.story} type="audio/mpeg" />
              متصفحك لا يدعم تشغيل الملفات الصوتية.
            </audio>
          </div>
          <div className="story-actions">
            <Button 
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(story);
              }}
              icon={<Edit size={16} />}
            >
              تعديل
            </Button>
            <Button 
              variant="danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(story);
              }}
              icon={<Trash2 size={16} />}
            >
              حذف
            </Button>
            <Button 
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                onViewReviews(story.id);
              }}
              icon={<Star size={16} />}
            >
              التقييمات
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageUpload = ({ onUpload, currentImage, label, loading }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "image/*",
    onDrop: onUpload,
    disabled: loading
  });

  return (
    <div className="image-upload-container">
      <label>{label}</label>
      <div {...getRootProps()} className={`image-upload ${isDragActive ? 'drag-active' : ''} ${loading ? 'loading' : ''}`}>
        <input {...getInputProps()} />
        {loading ? (
          <div className="upload-loading">
            <div className="loading-spinner"></div>
            <span>جاري التحميل...</span>
          </div>
        ) : currentImage ? (
          <img src={currentImage} alt="Preview" className="preview-image" />
        ) : (
          <div className="upload-placeholder">
            <span>اسحب الصورة هنا أو انقر للتحميل</span>
          </div>
        )}
      </div>
    </div>
  );
};

const AudioUpload = ({ onUpload, currentFile, label, loading }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ".mp3",
    onDrop: onUpload,
    disabled: loading
  });

  return (
    <div className="audio-upload-container">
      <label>{label}</label>
      <div {...getRootProps()} className={`audio-upload ${isDragActive ? 'drag-active' : ''} ${loading ? 'loading' : ''}`}>
        <input {...getInputProps()} />
        {loading ? (
          <div className="upload-loading">
            <div className="loading-spinner"></div>
            <span>جاري التحميل...</span>
          </div>
        ) : currentFile ? (
          <div className="file-preview">
            <span>تم اختيار الملف: {currentFile}</span>
          </div>
        ) : (
          <div className="upload-placeholder">
            <span>اسحب ملف MP3 هنا أو انقر للتحميل</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Toast = ({ message, type, onClose }) => (
  <div className={`toast toast-${type}`}>
    <span>{message}</span>
    <button onClick={onClose}>&times;</button>
  </div>
);

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [album, setAlbum] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newStory, setNewStory] = useState({ name: "", image: null, longImage: null, storyFile: null });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [storyToEdit, setStoryToEdit] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingLongImage, setUploadingLongImage] = useState(false);
  const [uploadingStoryFile, setUploadingStoryFile] = useState(false);
  const [editUploadingImage, setEditUploadingImage] = useState(false);
  const [editUploadingLongImage, setEditUploadingLongImage] = useState(false);
  const [editUploadingStoryFile, setEditUploadingStoryFile] = useState(false);

  const onTogglePremium = async (storyId, currentStatus) => {
    try {
      const response = await apis.toggleStoryPremium(storyId, !currentStatus);
      console.log("Premium status updated:", response);
      setStories((prevStories) =>
        prevStories.map((story) =>
          story.id === storyId ? { ...story, premium: !currentStatus } : story
        )
      );
    } catch (error) {
      console.error("Error toggling premium status:", error);
    }
  };

  const handleEditOpen = (story) => {
    setStoryToEdit(story);
    setNewStory({
      name: story.name,
      image: story.image,
      longImage: story.longImage,
      storyFile: story.story,
    });
    setIsEditModalOpen(true);
  };
  
  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setStoryToEdit(null);
  };
  
  const handleEditStory = async () => {
    if (!newStory.name || !newStory.image || !newStory.longImage || !newStory.storyFile) {
      setError("الرجاء إدخال جميع البيانات");
      return;
    }
  
    try {
      const response = await apis.editStory(storyToEdit.id,
         newStory.name,
        newStory.image,
         newStory.longImage,
        newStory.storyFile,
      );
      console.log(response);
      setIsEditModalOpen(false);
      fetchStories(storyToEdit.album);
    } catch (e) {
      console.error("Error updating story:", e);
      setError("فشل تعديل القصة");
    }
  };
  
  const fetchStories = async (albumId) => {
    try {
      const data = await apis.getStories(albumId);
      setStories(data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching stories:", error);
      setLoading(false);
      setError("فشل في تحميل القصص");
    }
  };

  useEffect(() => {
    const albumId = new URLSearchParams(location.search).get("id");
    const albumName = new URLSearchParams(location.search).get("name");
    
    if (albumId) {
      setAlbum({ id: albumId, name: albumName || 'القصص' });
      fetchStories(albumId);
    } else {
      setError("لم يتم تحديد الألبوم");
      setLoading(false);
    }
  }, [location.search]);

  const handleAddOpen = () => {
    setNewStory({ name: "", image: null, longImage: null, storyFile: null });
    setIsAddModalOpen(true);
  };

  const handleAddClose = () => {
    setIsAddModalOpen(false);
  };

  const handleAddStory = async () => {
    const albumId = new URLSearchParams(location.search).get("id");
    if (!newStory.name || !newStory.image || !newStory.longImage || !newStory.storyFile) {
      setError("الرجاء إدخال جميع البيانات");
      return;
    }

    try {
      const response = await apis.addStory(newStory.name, newStory.image, newStory.longImage, newStory.storyFile, albumId);
      console.log(response);
      setIsAddModalOpen(false);
      fetchStories(albumId);
    } catch (e) {
      console.error(e);
      setError("فشل في إضافة القصة");
    }
  };

  const handleDeleteConfirmation = (story) => {
    setStoryToDelete(story);
    setDeleteConfirmationOpen(true);
  };

  const handleDelete = async () => {
    if (storyToDelete) {
      try {
        await apis.deleteStory(storyToDelete.id);
        fetchStories(storyToDelete.album);
        setDeleteConfirmationOpen(false);
        setStoryToDelete(null);
      } catch (error) {
        console.error("Error deleting story:", error);
        setError("فشل حذف القصة");
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setStoryToDelete(null);
  };

  const handleViewReviews = (storyId) => {
    navigate(`/reviews?id=${storyId}`);
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchStories(album.id);
  };

  // Add these functions for edit modal uploads
  const handleEditImageUpload = async (acceptedFiles) => {
    setEditUploadingImage(true);
    try {
      const response = await apis.uploadFile(acceptedFiles[0]);
      setNewStory(prev => ({ ...prev, image: response.url }));
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("فشل في تحميل الصورة");
    } finally {
      setEditUploadingImage(false);
    }
  };

  const handleEditLongImageUpload = async (acceptedFiles) => {
    setEditUploadingLongImage(true);
    try {
      const response = await apis.uploadFile(acceptedFiles[0]);
      setNewStory(prev => ({ ...prev, longImage: response.url }));
    } catch (error) {
      console.error("Error uploading long image:", error);
      setError("فشل في تحميل صورة الخلفية");
    } finally {
      setEditUploadingLongImage(false);
    }
  };

  const handleEditStoryFileUpload = async (acceptedFiles) => {
    setEditUploadingStoryFile(true);
    try {
      const response = await apis.uploadFile(acceptedFiles[0]);
      setNewStory(prev => ({ 
        ...prev, 
        storyFile: response.url,
        storyFileName: acceptedFiles[0].name 
      }));
    } catch (error) {
      console.error("Error uploading story file:", error);
      setError("فشل في تحميل ملف القصة");
    } finally {
      setEditUploadingStoryFile(false);
    }
  };

  // Check if any upload is in progress
  const isAnyUploadInProgress = 
    uploadingImage || 
    uploadingLongImage || 
    uploadingStoryFile || 
    editUploadingImage || 
    editUploadingLongImage || 
    editUploadingStoryFile;

  if (loading) {
    return (
      <div className="stories-page">
        <header className="stories-header">
          <div className="header-content">
            <h1>{album ? album.name : 'القصص'}</h1>
            <p>{stories.length} قصة في هذا الألبوم</p>
          </div>
          <div className="header-actions">
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              icon={<ArrowLeft size={18} />}
            >
              العودة للألبومات
            </Button>
            <Button 
              onClick={handleRefresh}
              variant="refresh"
              icon={<RefreshCw size={18} />}
            >
              تحديث
            </Button>
            <Button 
              onClick={handleAddOpen}
              icon={<Plus size={18} />}
            >
              إضافة قصة
            </Button>
          </div>
        </header>

        <div className="stories-grid">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="story-card-skeleton">
              <div className="skeleton-image"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="stories-page">
      <header className="stories-header">
        <div className="header-content">
          <h1>{album ? album.name : 'القصص'}</h1>
          <p>{stories.length} قصة في هذا الألبوم</p>
        </div>
        <div className="header-actions">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="secondary"
            icon={<ArrowRight size={18} />}
          >
            العودة للألبومات
          </Button>
          <Button 
            onClick={handleRefresh}
            variant="refresh"
            icon={<RefreshCw size={18} />}
          >
            تحديث
          </Button>
          <Button 
            onClick={handleAddOpen}
            icon={<Plus size={18} />}
          >
            إضافة قصة
          </Button>
        </div>
      </header>

      <div className="stories-grid">
        {stories.map(story => (
          <StoryCard 
            key={story.id}
            story={story}
            onEdit={handleEditOpen}
            onDelete={handleDeleteConfirmation}
            onTogglePremium={onTogglePremium}
            onViewReviews={handleViewReviews}
          />
        ))}
      </div>

      <Modal
        isOpen={isAddModalOpen}
        onClose={handleAddClose}
        title="إضافة قصة جديدة"
      >
        <div className="story-form">
          <Input
            label="اسم القصة"
            value={newStory.name}
            onChange={(e) => setNewStory(prev => ({ ...prev, name: e.target.value }))}
          />
          <ImageUpload
            label="صورة القصة"
            onUpload={async(acceptedFiles) => {
              setUploadingImage(true);
              try {
                const response = await apis.uploadFile(acceptedFiles[0]);
                setNewStory(prev => ({ ...prev, image: response.url }));
              } catch (error) {
                console.error("Error uploading image:", error);
                setError("فشل في تحميل الصورة");
              } finally {
                setUploadingImage(false);
              }
            }}
            currentImage={newStory.image}
            loading={uploadingImage}
          />
          <ImageUpload
            label="صورة الخلفية"
            onUpload={async(acceptedFiles) => {
              setUploadingLongImage(true);
              try {
                const response = await apis.uploadFile(acceptedFiles[0]);
                setNewStory(prev => ({ ...prev, longImage: response.url }));
              } catch (error) {
                console.error("Error uploading long image:", error);
                setError("فشل في تحميل صورة الخلفية");
              } finally {
                setUploadingLongImage(false);
              }
            }}
            currentImage={newStory.longImage}
            loading={uploadingLongImage}
          />
          <AudioUpload
            label="ملف القصة"
            onUpload={async(acceptedFiles) => {
              setUploadingStoryFile(true);
              try {
                const response = await apis.uploadFile(acceptedFiles[0]);
                setNewStory(prev => ({ 
                  ...prev, 
                  storyFile: response.url,
                  storyFileName: acceptedFiles[0].name 
                }));
              } catch (error) {
                console.error("Error uploading story file:", error);
                setError("فشل في تحميل ملف القصة");
              } finally {
                setUploadingStoryFile(false);
              }
            }}
            currentFile={newStory.storyFileName}
            loading={uploadingStoryFile}
          />
          <div className="form-actions">
            <Button onClick={handleAddClose} variant="secondary">
              إلغاء
            </Button>
            <Button 
              onClick={handleAddStory}
              disabled={isAnyUploadInProgress}
            >
              {isAnyUploadInProgress ? (
                <>
                  <div className="loading-spinner"></div>
                  جاري الحفظ...
                </>
              ) : (
                "إضافة القصة"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
        title="تعديل القصة"
      >
        <div className="story-form">
          <Input
            label="اسم القصة"
            value={newStory.name}
            onChange={(e) => setNewStory(prev => ({ ...prev, name: e.target.value }))}
          />
          <ImageUpload
            label="صورة القصة"
            onUpload={handleEditImageUpload}
            currentImage={newStory.image}
            loading={editUploadingImage}
          />
          <ImageUpload
            label="صورة الخلفية"
            onUpload={handleEditLongImageUpload}
            currentImage={newStory.longImage}
            loading={editUploadingLongImage}
          />
          <AudioUpload
            label="ملف القصة"
            onUpload={handleEditStoryFileUpload}
            currentFile={newStory.storyFileName}
            loading={editUploadingStoryFile}
          />
          <div className="form-actions">
            <Button onClick={handleEditClose} variant="secondary">
              إلغاء
            </Button>
            <Button 
              onClick={handleEditStory}
              disabled={isAnyUploadInProgress}
            >
              {isAnyUploadInProgress ? (
                <>
                  <div className="loading-spinner"></div>
                  جاري الحفظ...
                </>
              ) : (
                "حفظ التعديلات"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteConfirmationOpen}
        onClose={handleDeleteCancel}
        title="تأكيد الحذف"
      >
        <div className="delete-confirmation">
          <p>هل أنت متأكد أنك تريد حذف هذه القصة؟</p>
          <div className="form-actions">
            <Button onClick={handleDeleteCancel} variant="secondary">
              إلغاء
            </Button>
            <Button onClick={handleDelete} variant="danger">
              حذف
            </Button>
          </div>
        </div>
      </Modal>

      {error && (
        <Toast
          message={error}
          type="error"
          onClose={() => setError("")}
        />
      )}
    </div>
  );
};

export default Stories;
